import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Users, MessageSquare } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

const SOURCE_LABEL = {
  formal_support: "Formal support",
  informal_support: "Informal support",
  external_referral: "External referral",
  support: "Support",
};

export default function Contacts() {
  const [enquiries, setEnquiries] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("directory");

  useEffect(() => {
    Promise.all([
      api.get("/contacts").then((r) => setEnquiries(r.data)),
      api.get("/directory/people").then((r) => setDirectory(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const filteredDirectory = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return directory;
    return directory.filter((p) =>
      [p.name, p.agency, p.role, p.phone, p.email, p.client_name].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [q, directory]);

  const filteredEnquiries = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return enquiries;
    return enquiries.filter((e) =>
      [e.full_name, e.email, e.subject, e.message].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [q, enquiries]);

  return (
    <div className="fade-in" data-testid="contacts-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">CRM</div>
          <h2 className="font-serif text-3xl text-njas-navy">Contacts</h2>
          <p className="text-sm text-njas-muted mt-1">People captured across the platform — support workers, agencies, family, and public enquiries.</p>
        </div>
        <div className="relative w-full max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-njas-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, agency, phone, email…" className={`${inputCls} pl-9`} data-testid="contacts-search" />
        </div>
      </div>

      <div className="border-b border-[var(--njas-border)] mb-5">
        <div className="flex gap-1">
          <TabBtn active={tab === "directory"} onClick={() => setTab("directory")} icon={Users} label={`Directory (${directory.length})`} testid="tab-directory" />
          <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} icon={MessageSquare} label={`Public enquiries (${enquiries.length})`} testid="tab-enquiries" />
        </div>
      </div>

      {tab === "directory" && (
        <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Name / Agency</th>
                  <th className="text-left px-5 py-3">Role</th>
                  <th className="text-left px-5 py-3">Phone</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Type</th>
                  <th className="text-left px-5 py-3">Linked client</th>
                  <th className="text-left px-5 py-3">Added</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-njas-muted">Loading…</td></tr>}
                {!loading && filteredDirectory.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-njas-muted">
                    No people in the directory yet. Fill out a <strong>Formal Support</strong>, <strong>Informal Support</strong> or <strong>External Referral</strong> form on any client and they will appear here.
                  </td></tr>
                )}
                {filteredDirectory.map((p) => (
                  <tr key={p.id} className="border-t border-[var(--njas-border)] hover:bg-njas-bg-alt/40" data-testid={`directory-row-${p.id}`}>
                    <td className="px-5 py-3">
                      <div className="text-njas-navy font-medium">{p.name}</div>
                      {p.agency && p.agency !== p.name && <div className="text-xs text-njas-muted">{p.agency}</div>}
                    </td>
                    <td className="px-5 py-3 text-njas-text">{p.role || "—"}</td>
                    <td className="px-5 py-3">{p.phone ? <a href={`tel:${p.phone}`} className="text-njas-navy hover:underline">{p.phone}</a> : <span className="text-njas-muted">—</span>}</td>
                    <td className="px-5 py-3">{p.email ? <a href={`mailto:${p.email}`} className="text-njas-navy hover:underline">{p.email}</a> : <span className="text-njas-muted">—</span>}</td>
                    <td className="px-5 py-3"><span className="inline-block px-2 py-0.5 bg-njas-sage-tint text-njas-sage-hover text-[10px] uppercase tracking-wider rounded">{SOURCE_LABEL[p.source_type] || p.source_type}</span></td>
                    <td className="px-5 py-3">
                      {p.client_id ? <Link to={`/dashboard/clients/${p.client_id}`} className="text-njas-navy hover:underline">{p.client_name || "Client"}</Link> : <span className="text-njas-muted">—</span>}
                    </td>
                    <td className="px-5 py-3 text-njas-muted">{p.created_at ? new Date(p.created_at).toLocaleDateString("en-AU") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "enquiries" && (
        <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Subject</th>
                  <th className="text-left px-5 py-3">Message</th>
                  <th className="text-left px-5 py-3">Received</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-njas-muted">Loading…</td></tr>}
                {!loading && filteredEnquiries.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-njas-muted">No public enquiries yet.</td></tr>}
                {filteredEnquiries.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--njas-border)] align-top hover:bg-njas-bg-alt/60" data-testid={`contact-row-${r.id}`}>
                    <td className="px-5 py-3">{r.full_name}</td>
                    <td className="px-5 py-3 text-njas-muted">{r.email}</td>
                    <td className="px-5 py-3 text-njas-text">{r.subject}</td>
                    <td className="px-5 py-3 text-njas-muted max-w-md truncate" title={r.message}>{r.message}</td>
                    <td className="px-5 py-3 text-njas-muted">{new Date(r.created_at).toLocaleDateString("en-AU")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active ? "border-njas-navy text-njas-navy" : "border-transparent text-njas-muted hover:text-njas-navy"
      }`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
